/**
 * Polish (`pl`) string catalogue — mirrors the key shape of `./en.ts`. Brand
 * names (`OVERVOLT`, `Sniper Rail`, `Arc Coil`, `Cryo Emitter`, `Splash
 * Mortar`, `Pulse Turret`, `Pylon`, `Flux`) stay in English; descriptive
 * copy translates into terse Polish.
 */

import type { Catalogue } from './en';

export const pl: Catalogue = {
  meta: {
    name: 'Polski',
    code: 'PL',
  },

  towers: {
    pulse: {
      label: 'Pulse Turret',
      short: 'PULS',
      role: 'Neutralny generalista. Pewne obrażenia w każdym miejscu ścieżki.',
    },
    arc: {
      label: 'Arc Coil',
      short: 'ARC',
      role: 'Sprzężony z siecią. Mocniejszy przy nadwyżce Pylonów, słabszy przy spadku napięcia.',
    },
    cryo: {
      label: 'Cryo Emitter',
      short: 'CRYO',
      role: 'Malarz wąskich gardeł. Mały zasięg — spowalnia i znaczy każdego wroga w jednym punkcie.',
    },
    mortar: {
      label: 'Splash Mortar',
      short: 'MOŹDZ.',
      role: 'Ogień pośredni. Postaw go DALEJ od ścieżki — z bliska to martwa strefa.',
    },
    sniper: {
      label: 'Sniper Rail',
      short: 'SNIPER',
      role: 'Przeszywający strzał. Silny na długich prostych; w zakrętach słaby.',
    },
    pylon: {
      label: 'Pylon',
      short: 'PYLON',
      role: 'Zasilanie. Postaw ich dość, by pokryć zapotrzebowanie wież (i nadwyżkę dla Arc).',
    },
    wall: {
      label: 'Wall Node',
      short: 'MUR',
      role: 'Tani blokujący — tylko w trybie Maze. Kształtuje trasę wrogów.',
    },
  },

  roleHints: {
    pulse: 'Pulse Turret: pewny wybór — solidne obrażenia w każdym miejscu ścieżki.',
    arc: 'Arc Coil: rośnie wraz z nadwyżką mocy. Buduj więcej Pylonów dla silniejszego łańcucha.',
    cryo: 'Cryo Emitter: precyzyjne malowanie wąskiego gardła. Spowolnij i oznacz parę pól w ścisku.',
    mortar: 'Splash Mortar: ogień pośredni. Postaw go DALEJ od ścieżki — z bliska nie trafi.',
    sniper: 'Sniper Rail: przeszywający strzał. Ustaw na długiej prostej dla maksymalnego efektu.',
    pylon: 'Power Pylon: zasila sieć. Postaw dość, by pokryć pobór wież.',
    wall: 'Mur: tani blokujący — kształtuje trasę, którą muszą iść wrogowie.',
  },

  hints: {
    build:
      'Postaw wieżę: wybierz jedną poniżej (lub 1), potem kliknij świecące pole przy ścieżce.',
    placed: 'Dobrze. Dodaj kolejną i naciśnij spację, by puścić falę.',
    wave1: 'Wieże strzelają same. Pilnuj, by pasek mocy nie wpadł w czerwoną strefę.',
    select: 'Kliknij postawioną wieżę, aby ją zaznaczyć — U ulepsza, S sprzedaje.',
    brownout: 'Spadek mocy! Wieże spowolnione. Postaw Pylon (3), by dodać energii.',
  },

  contextHints: {
    overload:
      'Overload: podkręć następną falę dla większych nagród — przy większym ryzyku. Wolny start? Podkręć dla szybszego Flux.',
    profileKey: 'Naciśnij P, by otworzyć Profil — Flux odblokuje nowe wieże, zdolności i tryby.',
    endlessMod: 'Endless rotuje MODYFIKATORY fal — dostosuj swoją kompozycję.',
    puzzleHover:
      'Tryb Puzzle: ręcznie zrobione poziomy logiczne z murami blokującymi linię widzenia. Zupełnie inna gra.',
  },

  hud: {
    buildZone: 'BUDUJ',
    waveZone: 'FALA',
    abilitiesZone: 'ZDOLNOŚCI',
    overloadZone: 'OVERLOAD',

    startWave: 'START FALI',
    nextWave: 'NASTĘPNA FALA',
    pause: 'PAUZA',
    resume: 'WZNÓW',
    restart: 'RESTART',
    deployHint: 'start [SPACE]',
    holdHint: 'pauza [SPACE]',
    rebootHint: 'reset [R]',
    nextWaveHint: 'NASTĘPNA FALA',

    audioOn: 'DŹWIĘK WŁ',
    audioOff: 'DŹWIĘK WYŁ',

    credits: 'KREDYTY',
    core: 'RDZEŃ',
    wave: 'FALA',
    endless: 'ENDLESS',
    score: 'WYNIK',
    powerGrid: 'SIEĆ',

    runMode: 'TRYB',
    difficulty: 'POZIOM',
    profile: 'PROFIL',
    map: 'MAPA',
    clickHint: '▷ klik',

    locked: 'BLOKADA',
    lockedShort: 'BLOKADA',
    lockedTooltip:
      'BLOKADA — odblokuj za ✦{cost} Flux w Profilu (naciśnij P).',
    lockedTowerNotice: '{tower} zablokowany',
    lockedAbility: 'Zdolność zablokowana',
    lockedOverload: 'Poziom Overload zablokowany',
    lockedMode: 'Tryb {mode} zablokowany',
    lockedDifficulty: 'Hard zablokowany',

    cheatPip: '🔓 CHEAT',
    profilePip: '👤 {name}',

    overloadIdle: '— overload działa od fali 1 —',
    overloadTier: 'TIER {tier} · {name}',
    overloadActive: '◉ AKTYWNE {sec}s',

    maxLevel: 'MAX POZIOM',
    upgrade: 'ULEPSZ L{level}',
    sell: 'SPRZEDAJ',
    target: 'CEL: {mode}',
    cycleTarget: 'zmień · [T]',
    sellHint: '+{refund}c · [S]',
    upgradeHint: '{cost}c · [U]',
    overclock: 'OVERCLOCK',
    overclockOn: 'OVERCLOCK ✦ WŁ',

    // Sprint 69 (i18n-gaps-extract-s69) — extracted from render.ts / hud.ts.
    buildHintIntro: "FAZA BUDOWY  //  wybierz wieżę [1·2·3·4·5], postaw ją i RUSZ FALĘ [SPACE]",
    buildHintReinforce: "FAZA BUDOWY  //  wzmocnij siatkę i RUSZ FALĘ {wave} [SPACE]",
    mazeRoute: "T R A S A   L A B I R Y N T U",
    tiles: "POLA",
    abilityActive: "◉ AKTYWNE {sec}s",
    costSuffix: '{cost}c',
    overclockPip: 'OC ×{n}',
    powerReadout: '{demand} / {supply}W  ·  {pct}%',
    towerCostDraw: '{cost}c {power}w',
    pylonCostSupply: '{cost}c +{supply}w',
    wallCost: '{cost}c 0w',
    endlessModBadge: 'MOD · {label}',
    startWaveOverload: '⚡{n} {tierName}',
    waveOverloadPip: '⚡{n} {tierName}',
    overclockNoticeOn: "{tower} Overclock WŁ",
    overclockNoticeOff: "{tower} Overclock WYŁ",

    tierMult: {
      hp: "HP",
      sp: "SP",
      cn: "IL",
      gd: "ZL",
      sc: "PK",
    },
    spawnShort: {
      bit: "Bit",
      packet: "Pkt",
      daemon: "Dmn",
      fork: "Fork",
      cipher: "Cph",
      patch: "Pch",
      buffer: "Buf",
      streak: "Stk",
      kernel: "KERNEL",
    },
  },

  modes: {
    campaign: 'KAMPANIA',
    endless: 'ENDLESS',
    maze: 'MAZE',
    puzzle: 'PUZZLE',
  },
  modeSub: {
    campaign: '10 fal · wygraj',
    endless: '✦ {cost}',
    maze: '✦ {cost}',
    puzzle: 'wybierz poziom',
  },
  modeBlurb: {
    campaign: '10 fal. Boss w 10. fali. Wygraj, a awansujesz.',
    endless: 'Ta sama mapa. Co fala nowy modyfikator. Adaptuj się albo przeciekniesz.',
    maze: 'Otwarte pole. Murami kształtujesz trasę. Spowolnij ich lub odbierz im łup.',
    puzzle: '10 ręcznie projektowanych poziomów. Znajdź jedyną wygrywającą kompozycję.',
  },
  modeCard: {
    campaign: {
      tagline: 'Naucz się sieci, pokonaj bossa.',
      bullet1: '10 ręcznie strojonych fal + boss',
      bullet2: 'Stała trasa S — buduj, fala, ulepsz',
      bullet3: 'Wygrana odblokowuje Endless na tej mapie',
      length: '~8 min',
      skill: 'Trenuje kolejność budowy',
    },
    endless: {
      tagline: 'Adaptuj się albo przecieknie. Bez końca.',
      bullet1: 'Gra Kampanię 1–10, potem rośnie wiecznie',
      bullet2: 'Nowy modyfikator co falę',
      bullet3: 'Koniec, gdy Rdzeń padnie',
      length: '~30 min',
      skill: 'Trenuje długie zwroty',
    },
    maze: {
      tagline: 'Budujesz labirynt, którym muszą iść.',
      bullet1: 'Otwarte pole — bez rysowanej trasy',
      bullet2: 'Wieże i tanie mury kształtują trasę',
      bullet3: 'Dłuższa trasa = więcej czasu w ogniu',
      length: '~12 min',
      skill: 'Trenuje routing przestrzenny',
    },
    puzzle: {
      tagline: 'Jeden układ wygrywa. Znajdź go.',
      bullet1: '10 poziomów, jedno rozwiązanie',
      bullet2: 'Stały budżet i stała fala',
      bullet3: 'Mury blokujące widok + limit wież',
      length: 'po ~2 min',
      skill: 'Trenuje dedukcję',
    },
  },
  difficulty: {
    easy: 'ŁATWY',
    normal: 'NORMALNY',
    hard: 'TRUDNY',
  },
  difficultySub: {
    easy: 'łagodny',
    normal: 'wyważony',
    hard: '✦ {cost}',
  },

  overlay: {
    paused: 'PAUZA',
    pauseHint: 'NACIŚNIJ  SPACJĘ  BY  WZNOWIĆ',

    missionComplete: '// MISJA WYKONANA',
    gridSecured: 'SIEĆ ZABEZPIECZONA',
    wavesRepelled: 'Odparto wszystkie {total} fale szturmu.',
    continueEndless: 'KONTYNUUJ  →  ENDLESS',
    unlockEndless: 'odblokuj Endless w Profilu, aby kontynuować',
    restartCampaign: 'lub naciśnij  R  by restartować kampanię',

    systemFailure: '// AWARIA SYSTEMU',
    coreBreached: 'RDZEŃ PRZEŁAMANY',
    endlessEnded: 'Bieg Endless zakończony na fali {wave}.',
    reactorOverrun: 'Reaktor przełamany w fali {wave} z {total}.',
    runItBack: 'NACIŚNIJ  R  BY  POWTÓRZYĆ',

    finalScore: 'WYNIK KOŃCOWY',
    newHighScore: '★  NOWY REKORD  ★',
    newEndlessBest: '★  NOWY REKORD ENDLESS  ★',
    highScore: 'REKORD',
    endlessBest: 'REKORD ENDLESS',

    // Sprint 69 — top-banner countdown overlays drawn by render.ts.
    aftershock: "// REZONANS — DANINA SIECI  {sec}s //",
    overcharge: "// OVERCHARGE — SIEĆ WZMOCNIONA  {sec}s //",
    brownout: "// AWARIA SIECI — WIEŻE SPOWOLNIONE //",
  },

  profile: {
    title: 'PROFIL',
    subtitle: '// META-PROGRES',
    closeHint: 'naciśnij  P  lub  ESC  aby zamknąć',
    tabUnlocks: 'ODBLOKOWANIA',
    tabScoreboard: 'TABELA WYNIKÓW',
    flux: 'FLUX',
    unlockTree: 'DRZEWO ODBLOKOWAŃ',
    unlockedCount: '{owned} / {total} ODBLOKOWANO',
    lifetime: 'ŁĄCZNIE',

    branchArsenal: 'ARSENAŁ',
    branchModes: 'TRYBY',
    branchChallenge: 'WYZWANIE',
    branchCosmetic: 'KOSMETYKA',

    statRunsPlayed: 'BIEGI',
    statCampaignWins: 'WYGRANE KAMPANII',
    statBestScore: 'NAJLEPSZY WYNIK',
    statDeepestEndless: 'NAJDALSZY ENDLESS',
    statTotalFlux: 'ŁĄCZNY FLUX',

    personalBests: 'REKORDY OSOBISTE',
    waveLabel: 'FALA {wave}',

    runHistory: 'HISTORIA BIEGÓW',
    noRuns: 'Brak biegów — skończ jeden, aby zacząć wspinaczkę.',
    earlierRuns: '+ {n} wcześniejszy bieg',
    earlierRunsPlural: '+ {n} wcześniejszych biegów',
    colRun: 'BIEG',
    colMode: 'TRYB · POZIOM',
    colResult: 'WYNIK',
    colOverload: 'OVL',
    colScore: 'PUNKTY',
    colDeltaBest: 'Δ NAJL.',
    colFlux: 'FLUX',
    starBest: '★ NAJLEPSZY',

    statusUnlocked: 'ODBLOKOWANE',
    statusOwned: 'POSIADANE',
    statusRequiresPrereq: 'Najpierw {prereq}',
    statusFluxShort: 'Brak ✦{short} Flux',
    statusAffordable: 'Dostępne — kliknij, aby odblokować',
    statusCost: '✦{cost} do odblokowania',
    buyAction: 'KUP  ▸',
    nodeReq: 'WYM: {prereq}',

    profileLauncher: 'PROFIL',
    profileKeyHint: '[ P ]',
    viewScoreboard: 'POKAŻ  TABELĘ',
    newPersonalBest: '★  NOWY REKORD OSOBISTY  ★',
    fromYourBest: 'OD TWOJEGO REKORDU',

    grantTower: 'WIEŻA',
    grantAbility: 'AKTYWNA ZDOLNOŚĆ',
    grantMode: 'TRYB GRY',
    grantDifficulty: 'POZIOM',
    grantOverload: 'POZIOM OVERLOAD {tier}',
    grantTheme: 'MOTYW PLANSZY',

    // Sprint 69 — unlock-tree hover preview.
    nodePreview: {
      towerMortar: "Broń strzału pośredniego. Postaw DALEKO od ścieżki — z bliska martwa strefa; pociski na klastry, szarpie pancerz.",
      towerSniper: "Lanca w linii ognia. Wyceluj w długą prostą — każdy wróg dostaje; w zakrętach słaba.",
      abilityAirstrike: "Jednorazowy wybuch na wybranym polu (45c). Wyczyść klaster lub uszczknij grubego wroga.",
      abilityEmp: "Plansza-szeroki stun (80c). Zamroź na chwilę wszystkich wrogów, gdy linia padnie.",
      modeEndless: "Graj poza falą 10 — proceduralne fale do upadku Jądra. Nowy ścieżka punktów.",
      modeMaze: "Otwarte pole budowy trasy. Wieże blokują kafle; wrogowie idą A*-wokół.",
      difficultyHard: "Trudniejsze fale; większe mnożniki złota i punktów. Odblokowuje górny pas Flux.",
      overloadCritical: "Overload tier 3 — odpal mocniej dla tłustszych mnożników punktów/Flux w następnej fali.",
      overloadMeltdown: "Overload tier 4 — najtwardsza fala wzorcowa. Top mnożniki; tylko dla solidnego buildu.",
      themeAmber: "Kosmetyczna paleta — ciepły terminal Amber CRT. Czysto kosmetyczne; bez efektu.",
      themePhosphor: "Kosmetyczna paleta — vintage zielony fosfor. Czysto kosmetyczne; bez efektu.",
    },
  },

  help: {
    eyebrow: '/ /  P R Z E W O D N I K',
    title: 'INSTRUKCJA OBSŁUGI',
    controls: 'STEROWANIE',
    powerGrid: 'SIEĆ ENERGETYCZNA',
    powerGridBody:
      'Broń pobiera moc; Pylony ją dostarczają. Gdy zapotrzebowanie przewyższa zasilanie, sieć spada w brownout i wszystkie wieże zwalniają — buduj Pylony, by strzelały dalej.',
    objective: 'CEL',
    objectiveBody:
      'Wyczyść wszystkie 15 fal kampanii, by wygrać; przegrywasz, gdy życie Rdzenia spadnie do zera. Tryb Endless trwa bez końca.',
    mazeMode: 'TRYB MAZE',
    mazeModeBody:
      'Trzeci tryb na otwartym polu: wieże blokują pola, na których stoją, a wrogowie obchodzą je A*, więc budujesz labirynt. Nie możesz całkowicie zamurować Rdzenia.',
    glitches: 'GLITCHE',
    towers: 'WIEŻE',
    abilities: 'ZDOLNOŚCI',
    replayIntro: '↺  POWTÓRZ INTRO',
    closeHint: 'Esc / H / ? lub klik w dowolnym miejscu zamyka',
  },

  intro: {
    eyebrow: '/ /  B R I E F I N G   S Y S T E M U',
    titleBig: 'OVERVOLT',
    subtitle: 'Broń Rdzenia',
    line1:
      'Skażone programy maszerują obwodem do twojego Rdzenia. Buduj wieże, by je zniszczyć, zanim się przebiją.',
    line2:
      '⚡ Każda wieża pobiera moc. Zbuduj za dużo i sieć padnie w brownout — wszystkie wieże zwalniają naraz. Buduj Pylony, by moc płynęła.',
    line3: 'Buduj w fazie spokoju, potem naciśnij Start fali. Przetrwaj 15 fal, by wygrać.',
    line4: 'Mysz buduje · 1–5 wybór · Spacja start/pauza · U ulepsz · S sprzedaj',
    gotIt: 'JASNE  →',
    gotItHint: 'naciśnij dowolny klawisz',
  },

  puzzle: {
    label: 'PUZZLE',
    levels: {
      'p0-first-light': {
        name: 'Pierwsze Światło',
        hint: 'Postaw Pulse obok korytarza. Każde pole w zasięgu wystarczy.',
      },
      'p1-long-lance': {
        name: 'Długa Lanca',
        hint: 'Jeden Sniper wyrównany z linią przeszyje każdego wroga.',
      },
      'p2-over-the-wall': {
        name: 'Ponad Murem',
        hint: 'Mur blokuje każdy bezpośredni promień. Pociski moździerza lecą łukiem.',
      },
      'p3-funhouse-mirror': {
        name: 'Krzywe Zwierciadło',
        hint: 'Sniper przeciśnięty przez szczelinę trafia obie linie.',
      },
      'p4-surplus-riches': {
        name: 'Bogactwo z Nadmiaru',
        hint: 'Zbuduj Pylony ponad pasem wolnym. Arc Coil żyje z nadwyżki.',
      },
      'p5-pinch-point': {
        name: 'Wąskie Gardło',
        hint: 'Cryo maluje ścisk; Pulses dookoła kończą grupę.',
      },
      'p6-cipher-gauntlet': {
        name: 'Cipher — Pas Trudności',
        hint: 'Moździerz miażdży pancerz; Cryo maluje zakręt; Sniper przeszywa dolne ramię. Użyj całej kompozycji.',
      },
      'p7-kernel-echo': {
        name: 'Echo Kernela',
        hint: 'Moździerze lecą ponad murami widzenia. Sniper przeciska z dołu. Pilnuj Power Drain.',
      },
      'p8-overclock-crunch': {
        name: 'Tłok Overclock',
        hint: 'Jeden Pulse nie nadąży. Overclockuj w decydującej chwili — stała szybkość nie wystarczy.',
      },
      'p9-pylon-pinch': {
        name: 'Ścisk Pylonów',
        hint: 'Skup Pylony między wieżami — limit aury nagradza ciasny układ.',
      },
    },
    briefing: {
      eyebrow: "BRIEFING PUZZLA",
      startBtn: "Start ▷",
      cancelBtn: "Anuluj",
      sectionConstraints: "OGRANICZENIA",
      sectionTowers: "WIEŻE",
      sectionWinCondition: "WARUNEK ZWYCIĘSTWA",
      budgetLabel: "Budżet złota",
      wavesLabel: "Fale",
      towerAllowed: "DOZWOLONA",
      towerForbidden: "ZABLOKOWANA",
      resultEyebrowWon: "PUZZLE UKOŃCZONE",
      resultEyebrowLost: "PUZZLE NIEUDANE",
      resultTitleWon: "ROZWIĄZANE!",
      resultTitleLost: "SPRÓBUJ ZNÓW",
      resultStatTowers: "WIEŻE",
      resultStatGold: "WYDANE ZŁOTO",
      resultStatLives: "POZOSTAŁE ŻYCIA",
      resultNextBtn: "Następny poziom ▷",
      resultReplayBtn: "Powtórz",
      resultResetBtn: "Resetuj próbę",
      resultBackBtn: "Powrót do wyboru",
    },

    // Sprint 69 — puzzle level-select overlay chrome.
    select: {
      subtitle: "Ręcznie tworzone poziomy logiczne — odgadnij jedyne zwycięskie rozłożenie.",
      close: "Zamknij",
      cardTitle: 'P{index}  {name}',
      lockedCard: "🔒 ZABLOKOWANE",
      solved: "✓ Rozwiązane",
      unsolved: "▷ Nierozwiązane",
      noSolve: "Brak rozwiązania",
      bestLine: "Najlepiej: {towers}t · {gold}g · {power}p",
      allowed: "Dozwolone: {list}",
      parPlaceholder: "par: — / — / —",
      parRow: "{towers}t · {gold}g · {power}p",
    },
  },

  localeChip:
 {
    label: 'JĘZYK',
    en: 'EN',
    cs: 'CS',
  },

  resume: {
    title: 'Wznowić poprzedni bieg?',
    resume: 'Wznów',
    newRun: 'Nowy bieg',
    hint: '[Enter] Wznów   ·   [Esc] Nowy',
    detail: "{mode} · {difficulty} · fala {wave} · {towers} wież · {gold}g · {lives}♥",
  },

  // -------- Sprint 69: Kernel boss UI chrome ---------------------------------
  boss: {
    kernelHeader: "⚠  KERNEL",
    phase: "FAZA {phase} · {name}",
    powerDrain: "⚡ ODPŁYW MOCY",
    kernelIncoming: "⚠  KERNEL  NADCHODZI  ⚠",
    phaseName: {
      boot: "BOOT SEQUENCE",
      fork: "FORK BOMB",
      panic: "KERNEL PANIC",
    },
  },

  // -------- Sprint 69: Map registry display strings --------------------------
  maps: {
    default: { label: "Datawash", tagline: "Otwarty S-zakręt — klasyczny układ OVERVOLT." },
    cramped: { label: "Switchyard", tagline: "Ciasne korytarze — każde pole walczy." },
    lines: { label: "Long Lines", tagline: "Długie proste — poprowadź lancę." },
    serpentine: { label: "Snake Run", tagline: "Ciasny wąż — Cryo painter rządzi." },
  },

  // -------- Sprint 69: Ability display names ---------------------------------
  abilities: {
    overcharge: { label: 'Overcharge' },
    airstrike: { label: 'Airstrike' },
    empBurst: { label: 'EMP Burst' },
  },

  // -------- Sprint 69: Overload tier names -----------------------------------
  overload: {
    tierName: {
      nominal: "Nominalny",
      charged: "Naładowany",
      surging: "Wzrastający",
      critical: "Krytyczny",
      meltdown: "Meltdown",
    },
  },

  // -------- Sprint 69: Endless modifier labels -------------------------------
  endlessMod: {
    armoredSurge: { label: "Pancerny szturm" },
    fastPack: { label: "Szybkie stado" },
    scatteredSpread: { label: "Rozproszenie" },
    streakRunners: { label: "Streak biegacze" },
    daemonRush: { label: "Atak Daemonów" },
  },

  // -------- Sprint 69: Enemy display names (Help roster) ---------------------
  enemies: {
    bit: { label: "Bit" },
    packet: { label: "Packet" },
    daemon: { label: "Daemon" },
    fork: { label: "Fork" },
    cipher: { label: "Cipher" },
    patch: { label: "Patch" },
    buffer: { label: "Buffer" },
    streak: { label: "Streak" },
    kernel: { label: "Kernel" },
  },

  // -------- Sprint 69: Help-panel enemy one-liners ---------------------------
  enemyRole: {
    bit: "Podstawowe mięso armatnie — tanie, wolne, kruche.",
    packet: "Szybki i słaby; pędzi do Jądra.",
    daemon: "Ciężka gąbka HP; mocno bije po przebiciu.",
    fork: "Po zniszczeniu dzieli się na dwa Bity.",
    cipher: "Opancerzony — rozerwij Korozją Moździerza.",
    patch: "Leczy pobliskie Glitche; zdejmij go najpierw.",
    buffer: "Regenerujący się tarczy; przebij burstem.",
    streak: "Sam sprintuje; opiera się slow. Pokryj ścieżkę.",
    kernel: "Bossowy faza — wysysa moc. Zniszcz przed następną fazą.",
  },

  // -------- Sprint 69: Help-panel ability one-liners -------------------------
  abilityRole: {
    overcharge: "Wzmocnij siatkę: każda wieża strzela szybciej przez chwilę. Zaplanuj wokół ciężkiej fali.",
    airstrike: "Jednorazowy wybuch na wybranym polu. Wyczyść klaster lub uszczknij grubego wroga.",
    empBurst: "Plansza-szeroki stun — wszyscy wrogowie chwilę zamarzną. Kup czas, gdy linia padnie.",
  },

  // -------- Sprint 69: Tooltip chrome ----------------------------------------
  tooltip: {
    levelOf: "LV {level}/{max}",
    credits: "{cost} kredytów",
    next: "DALEJ  →  LV {level}   {cost} kredytów",
    cooldown: "COOLDOWN  {sec}s",
    stat: {
      supply: "ZASILANIE  +{n}w do siatki",
      dmgRngCd: "DMG {damage}    RNG {range}    CD {cd}s",
      pwrChain: "PWR {power}w    ŁAŃCUCH {chain}",
      pwrAoe: "PWR {power}w    AOE {targets} cele",
      slow: "SLOW ×{factor}  na  {sec}s",
      brittle: "KRUCHE +{pct}% dmg od innych wież",
      pwrBlast: "PWR {power}w    BLAST {tiles} pól",
      pwrSingle: "PWR {power}w    1 cel",
    },
  },

  // -------- Sprint 69: Help-panel control-row labels -------------------------
  controls: {
    arm: "Wybierz Pulse / Arc / Pylon / Cryo / Moździerz / Sniper",
    mouse: "Postaw na polu · kliknij wieżę by wybrać",
    space: "Rusz falę · pauzuj falę",
    fast: "Przewijanie (1× ⇄ 2×)",
    upgradeSell: "Ulepsz / sprzedaj wybraną wieżę",
    target: "Zmień tryb celowania wybranej wieży",
    profile: "Otwórz Profil (drzewo odblokowań · ranking)",
    utility: "Anuluj · wycisz · restart biegu",
  },

  // -------- Sprint 69: Unlock-tree node display names ------------------------
  unlocks: {
    towerMortar: { name: "Splash Mortar" },
    towerSniper: { name: "Sniper Rail" },
    abilityAirstrike: { name: "Airstrike" },
    abilityEmp: { name: "EMP Burst" },
    modeEndless: { name: "Tryb Endless" },
    modeMaze: { name: "Tryb Maze" },
    difficultyHard: { name: "Trudność Hard" },
    overloadCritical: { name: "Overload: Critical" },
    overloadMeltdown: { name: "Overload: Meltdown" },
    themeAmber: { name: "Amber Terminal" },
    themePhosphor: { name: "Fosforowa Zieleń" },
  },

  // Sprint 73 — interaktywny samouczek (docs/TUTORIAL_DESIGN.md §6).
  tutorial: {
    button: {
      start: 'START SAMOUCZKA',
      startSub: 'Pierwszy raz? Weź udział →',
      replay: 'POWTÓRZ TURĘ',
      replaySub: 'Odśwież podstawy',
    },
    step: {
      welcome: {
        title: 'Witaj w OVERVOLT',
        body: 'Jesteś AI obrony uszkodzonego reaktora. Skorumpowane programy maszerują obwodem do twojego Core — zniszcz je, zanim się przebiją.',
      },
      path: {
        title: 'Ścieżka',
        body: 'Wrogowie zawsze podążają po tym S-zakręcie, od pola spawn po lewej do Core po prawej. Ty decydujesz, gdzie postawić wieże.',
      },
      firstPlace: {
        title: 'Postaw pierwszą wieżę',
        body: 'Pulse Turret kosztuje 50 kredytów i pobiera 4 mocy. Kliknij świecące pole obok ścieżki.',
      },
      wave1: {
        title: 'Wyślij pierwszą falę',
        body: 'Naciśnij START FALI. Twoja Pulse strzela automatycznie do celów w zasięgu.',
      },
      towerRoles: {
        title: 'Pięć wież, pięć ról',
        body: 'Pulse: niezawodna wszędzie. Sniper: długie proste. Moździerz: ogień pośredni, DALEJ od ścieżki. Arc: rośnie z nadwyżką mocy. Cryo: maluje jeden punkt zaciskowy.',
      },
      powerGrid: {
        title: 'Sieć energetyczna',
        body: 'Każda wieża pobiera moc. Gdy zapotrzebowanie przekracza podaż, sieć pada w brownout i wszystkie wieże zwalniają. Buduj Pylony — sąsiednie Pylony zwiększają szybkostrzelność.',
      },
      overloadDial: {
        title: 'Overload — ryzyko vs nagroda',
        body: 'Zwiększ pokrętło dla następnej fali: trudniejsi wrogowie, więcej Flux. Obniż, by grać bezpiecznie.',
      },
      overclock: {
        title: 'Podkręć wieżę',
        body: 'Włącz OVERCLOCK na wybranej wieży dla okna burst: szybszy ogień, większe pobór. Wygrana w burst, strata długoterminowa.',
      },
      abilities: {
        title: 'Aktywne zdolności',
        body: 'Trzy sloty: Overcharge wzmacnia całą sieć. Airstrike zostawia trwałą strefę spalenia. EMP Burst ogłusza wszystkich. Planuj — nie naciskaj w panice.',
      },
      mazePreview: {
        title: 'Tryb Maze',
        body: 'Brak ustalonej ścieżki — wieże blokują swoje pola, a wrogowie A*-routują wokół nich. Ty budujesz trasę, którą muszą przejść.',
      },
      puzzlePreview: {
        title: 'Tryb Puzzle',
        body: 'Ręcznie zrobione poziomy logiczne ze ścianami linii widzenia. Stały budżet, stała fala — znajdź jedno zwycięskie rozłożenie. Inna gra.',
      },
      wrapUp: {
        title: 'Jesteś gotowy',
        body: 'Biegi dają FLUX — wydaj go w Profilu, aby odblokować nowe wieże, zdolności i tryby. Zacznij od Kampanii; wróć po Endless, Maze i Puzzle.',
      },
    },
    ui: {
      eyebrow: 'KROK {n} / {total}',
      next: 'DALEJ →',
      back: '← WSTECZ',
      skip: 'POMIŃ TURĘ',
      play: 'GRAJ  ▸',
      skipConfirm: 'Pominąć turę? Możesz ją odtworzyć w każdej chwili z ekranu tytułowego.',
    },
  },
};
